import { QueryFunctionContext } from '@tanstack/react-query';

import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

// ── Tipos (espelham app/schemas/mcp.py do backend, /api/v1/mcp) ──────────────

export type McpTipoFerramenta = 'leitura' | 'proposta' | 'aplicacao';
export type McpNivel = 'geral' | 'restrita';
export type McpEstado = 'habilitada' | 'desabilitada' | 'removida';
export type McpResultado = 'ok' | 'negado' | 'erro';

export interface IMcpJsonSchema {
  type?: string;
  description?: string;
  title?: string;
  default?: unknown;
  properties?: Record<string, IMcpJsonSchema>;
  required?: string[];
  items?: IMcpJsonSchema;
  [chave: string]: unknown;
}

export interface IMcpGrupo {
  id: number;
  nome: string;
}

export interface IMcpFerramenta {
  nome: string;
  descricao: string;
  tipo: McpTipoFerramenta;
  nivel: McpNivel;
  exigencia: string;
  escalada: boolean;
  escrita: boolean;
  estado: McpEstado;
  grupos: IMcpGrupo[];
  /** Usuários liberados individualmente (id e nome), além dos grupos. */
  usuarios: IMcpGrupo[];
  /** Teto próprio por minuto, por sessão; null = vale só o global. */
  limite_por_min: number | null;
  limite_padrao: number;
  /** Texto que a IA lê no lugar da descrição do código; null = o do código. */
  descricao_ia: string | null;
  descricao_padrao: string;
  exige_confirmacao: boolean;
  visivel: boolean;
  somente_leitura: boolean;
  destrutiva: boolean;
  entrada_schema: IMcpJsonSchema;
}

export interface IMcpConfig {
  ativo: boolean;
  escrita_ativa: boolean;
  endpoint: string;
  origens_permitidas: string[];
  limite_leitura_por_min: number;
  limite_escrita_por_min: number;
  confirmacao_ttl_s: number;
  limite_corpo_bytes: number;
  limite_itens_resposta: number;
  protocolos: string[];
  servidor: Record<string, string>;
}

export interface IMcpResumoAuditoria {
  disponivel: boolean;
  total_24h: number;
  ok_24h: number;
  negado_24h: number;
  erro_24h: number;
  ultima_chamada: string | null;
  por_ferramenta_24h: { ferramenta: string; total: number }[];
}

export interface IMcpAcessoIp {
  ip_atual: string | null;
  entradas_ativas: number;
  regra: string;
}

export interface IMcpStatus {
  config: IMcpConfig;
  acesso_ip: IMcpAcessoIp;
  redis_ok: boolean;
  ferramentas: IMcpFerramenta[];
  auditoria: IMcpResumoAuditoria;
}

export interface IMcpAuditoriaItem {
  id: number;
  dt_criacao: string | null;
  usuario_id: number | null;
  usuario_nome: string | null;
  ferramenta: string;
  tipo: string;
  resultado: McpResultado;
  alvo_tipo: string | null;
  alvo_id: number | null;
  parametros: unknown;
  antes: unknown;
  depois: unknown;
  mensagem: string | null;
  confirmacao_id: string | null;
  endereco_ip: string | null;
}

export interface IMcpAuditoriaFiltros {
  ferramenta?: string;
  resultado?: McpResultado;
  limite?: number;
}

// Administração do acesso (Central de Permissões)

export interface IMcpChave {
  id: number;
  nome: string;
  prefixo: string;
  ativo: boolean;
  ultimo_uso: string | null;
  dt_criacao: string | null;
  sessoes_ativas: number;
}

/** A chave em texto claro só existe nesta resposta. */
export interface IMcpChaveCriada {
  id: number;
  nome: string;
  chave: string;
  sessoes_revogadas: number;
}

export interface IMcpSessaoItem {
  id: number;
  chave_nome: string | null;
  ip_login: string;
  user_agent: string | null;
  dt_criacao: string | null;
  dt_expira: string;
  ativa: boolean;
  revogada: boolean;
  usuario_id: number | null;
  usuario_nome: string | null;
  dt_expira_usuario: string | null;
}

export interface IMcpUsuarioAcesso {
  usuario_id: number;
  configurada: boolean;
  ativo: boolean;
  dt_definicao: string | null;
  ultimo_uso: string | null;
  sessoes_com_o_usuario: number;
}

/** `gerada` só vem quando o servidor gerou a senha: única vez em que ela aparece. */
export interface IMcpSenhaDefinida {
  gerada: string | null;
  status: IMcpUsuarioAcesso;
}

export interface IMcpFerramentaConfig {
  estado?: McpEstado;
  grupos?: number[];
  usuarios?: number[];
  /** 0 = sem teto próprio. */
  limite_por_min?: number;
  /** '' = volta à descrição do código. */
  descricao_ia?: string;
  exige_confirmacao?: boolean;
}

const BASE = '/api/v1/mcp';

// Painel e administração do MCP interno: só administrador, e só dos IPs da whitelist.
// O painel funciona com o MCP desligado (a tela mostra o estado, inclusive "desligado").
export const useMcp = () => {
  const getStatus = async (): Promise<IDataNoPagination<IMcpStatus>> => {
    const res = await api.get<IDataNoPagination<IMcpStatus>>(`${BASE}/status/`);
    return res.data;
  };

  const getAuditoria = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IMcpAuditoriaItem[]>> => {
    const filtros = (key.queryKey[1] ?? {}) as IMcpAuditoriaFiltros;
    const res = await api.get<IDataNoPagination<IMcpAuditoriaItem[]>>(`${BASE}/auditoria/`, {
      params: filtros,
    });
    return res.data;
  };

  // ── Chaves (login 1) e sessões ─────────────────────────────────────────────
  const getChaves = async (): Promise<IDataNoPagination<IMcpChave[]>> =>
    (await api.get<IDataNoPagination<IMcpChave[]>>(`${BASE}/chaves/`)).data;

  const criarChave = async (nome: string): Promise<IDataNoPagination<IMcpChaveCriada>> =>
    (await api.post<IDataNoPagination<IMcpChaveCriada>>(`${BASE}/chaves/`, { nome })).data;

  const girarChave = async (id: number): Promise<IDataNoPagination<IMcpChaveCriada>> =>
    (await api.post<IDataNoPagination<IMcpChaveCriada>>(`${BASE}/chaves/${id}/girar/`)).data;

  const ativarChave = async (id: number, ativo: boolean) =>
    (await api.patch(`${BASE}/chaves/${id}/`, { ativo })).data;

  const getSessoes = async (): Promise<IDataNoPagination<IMcpSessaoItem[]>> =>
    (await api.get<IDataNoPagination<IMcpSessaoItem[]>>(`${BASE}/sessoes/`)).data;

  const revogarSessao = async (id: number) => (await api.delete(`${BASE}/sessoes/${id}/`)).data;

  // ── Senha do MCP do usuário (login 2): painel do usuário na Central ────────
  const getAcessoUsuario = async (usuarioId: number): Promise<IMcpUsuarioAcesso> =>
    (await api.get<IDataNoPagination<IMcpUsuarioAcesso>>(`${BASE}/usuarios/${usuarioId}/acesso/`))
      .data.data;

  const definirSenhaUsuario = async (
    usuarioId: number,
    corpo: { senha?: string; gerar?: boolean },
  ): Promise<IMcpSenhaDefinida> =>
    (
      await api.put<IDataNoPagination<IMcpSenhaDefinida>>(
        `${BASE}/usuarios/${usuarioId}/senha/`,
        corpo,
      )
    ).data.data;

  const ativarAcessoUsuario = async (
    usuarioId: number,
    ativo: boolean,
  ): Promise<IMcpUsuarioAcesso> =>
    (
      await api.patch<IDataNoPagination<IMcpUsuarioAcesso>>(
        `${BASE}/usuarios/${usuarioId}/acesso/`,
        { ativo },
      )
    ).data.data;

  const removerAcessoUsuario = async (usuarioId: number): Promise<IMcpUsuarioAcesso> =>
    (
      await api.delete<IDataNoPagination<IMcpUsuarioAcesso>>(
        `${BASE}/usuarios/${usuarioId}/acesso/`,
      )
    ).data.data;

  // ── Ferramentas: habilitar, desabilitar, remover e atribuir a grupos ───────
  const configurarFerramenta = async (
    nome: string,
    corpo: IMcpFerramentaConfig,
  ): Promise<IMcpFerramenta> =>
    (
      await api.put<IDataNoPagination<IMcpFerramenta>>(
        `${BASE}/ferramentas/${encodeURIComponent(nome)}/`,
        corpo,
      )
    ).data.data;

  return {
    getStatus,
    getAuditoria,
    getChaves,
    criarChave,
    girarChave,
    ativarChave,
    getSessoes,
    revogarSessao,
    getAcessoUsuario,
    definirSenhaUsuario,
    ativarAcessoUsuario,
    removerAcessoUsuario,
    configurarFerramenta,
  };
};
